import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Tab,
  Tabs,
  Button,
  IconButton,
  Dialog,
  Slide,
  CircularProgress,
  Avatar,
  Chip,
  Paper,
  Badge,
  Divider,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getLoginUserData } from "../../redux/Apps/Lawyerapp/DashboardSlice/DashboardSlice";
import { backend_api } from "../../static/server";
import LawyerProfileForm from "./Froms/LawyerProfileForm";
import UserAvatar from "../../components/UserAvatar";
import {
  AccountBalanceRounded,
  BusinessRounded,
  CalendarMonthRounded,
  CasesRounded,
  CloseRounded,
  CreateRounded,
  DescriptionRounded,
  EmailRounded,
  GavelRounded,
  LocalPhoneRounded,
  LocationOnRounded,
  PersonRounded,
  SchoolRounded,
  StarRounded,
  TrendingUpRounded,
  VerifiedRounded,
  WorkHistoryRounded,
} from "@mui/icons-material";
import { useDialog } from "../../components/DialogContext";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

// Refined Info Card Component
const InfoCard = ({ icon: Icon, label, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      border: "1px solid #e5e7eb",
      borderRadius: 1.5,
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: "#d1d5db",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          backgroundColor: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18, color: "#64748b" }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: "#64748b",
            fontSize: "0.75rem",
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 0.5,
            display: "block",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#1e293b",
            fontSize: "0.875rem",
            fontWeight: 500,
            wordBreak: "break-word",
          }}
        >
          {value || "Not provided"}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const PracticeAreaCard = ({ area, cases, expertise }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      border: "1px solid #e5e7eb",
      borderRadius: 1.5,
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: "#d1d5db",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          backgroundColor: "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <GavelRounded sx={{ fontSize: 20, color: "#475569" }} />
      </Box>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          fontSize: "1rem",
          color: "#1e293b",
        }}
      >
        {area}
      </Typography>
    </Box>

    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
      <Typography
        variant="body2"
        sx={{ color: "#64748b", fontSize: "0.875rem" }}
      >
        Total Cases
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: "#1e293b",
          fontSize: "0.875rem",
        }}
      >
        {cases}
      </Typography>
    </Box>

    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          width: "100%",
          height: 8,
          backgroundColor: "#f1f5f9",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${expertise}%`,
            height: "100%",
            backgroundColor: "#3b82f6",
            borderRadius: 4,
            transition: "width 0.5s ease",
          }}
        />
      </Box>
    </Box>

    <Typography
      variant="caption"
      sx={{
        color: "#64748b",
        fontSize: "0.75rem",
        fontWeight: 500,
      }}
    >
      {expertise}% Success Rate
    </Typography>
  </Paper>
);

// Experience Item Component
const ExperienceItem = ({
  title,
  company,
  period,
  description,
  isEducation = false,
}) => (
  <Box sx={{ display: "flex", gap: 2.5, mb: 3 }}>
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        backgroundColor: "#f8fafc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {isEducation ? (
        <SchoolRounded sx={{ fontSize: 20, color: "#64748b" }} />
      ) : (
        <WorkHistoryRounded sx={{ fontSize: 20, color: "#64748b" }} />
      )}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          fontSize: "1rem",
          color: "#1e293b",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "#64748b",
          fontSize: "0.875rem",
          mb: 1,
        }}
      >
        {company} • {period}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            fontSize: "0.875rem",
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  </Box>
);

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      textAlign: "center",
      border: "1px solid #e5e7eb",
      borderRadius: 1.5,
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: "#d1d5db",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        backgroundColor: "#f8fafc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        mb: 1.5,
      }}
    >
      <Icon sx={{ fontSize: 24, color: "#64748b" }} />
    </Box>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        fontSize: "1.5rem",
        color: "#1e293b",
        mb: 0.5,
      }}
    >
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: "#64748b",
        fontSize: "0.75rem",
        fontWeight: 500,
      }}
    >
      {label}
    </Typography>
  </Paper>
);

const ProfilePage = () => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const [activeTab, setActiveTab] = useState(0);
  const [openLawyerForm, setOpenLawyerForm] = useState(false);
  const { token } = useSelector((data) => data.user);
  const { LoginUserData, loading } = useSelector((state) => state.dashboard);

  useEffect(() => {
    dispatch(
      getLoginUserData({
        url: `${backend_api}lawer/update-profile-lawer/`,
        token: token,
      })
    );
  }, [dispatch, token]);

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", p: 3 }}>
      {/* Professional Profile Header */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 3,
          border: "1px solid #e5e7eb",
          borderRadius: 2,
          backgroundColor: "#ffffff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
          <Badge
            badgeContent={<VerifiedRounded sx={{ fontSize: 16 }} />}
            color="primary"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            {loading ? (
              <Avatar sx={{ width: 80, height: 80 }}>
                <CircularProgress size={24} sx={{ color: "#64748b" }} />
              </Avatar>
            ) : (
              <UserAvatar
                firstName={LoginUserData?.first_name}
                imgSrc={LoginUserData?.avatar}
                height={"80px"}
                width={"80px"}
              />
            )}
          </Badge>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: "1.75rem",
                mb: 1,
                color: "#1e293b",
              }}
            >
              {LoginUserData?.first_name} {LoginUserData?.last_name}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Chip
                icon={<StarRounded sx={{ fontSize: 16 }} />}
                label="Premium Member"
                size="small"
                sx={{
                  backgroundColor: "#f1f5f9",
                  color: "#475569",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  border: "1px solid #e2e8f0",
                }}
              />
              <Chip
                icon={<VerifiedRounded sx={{ fontSize: 16 }} />}
                label="Verified Attorney"
                size="small"
                sx={{
                  backgroundColor: "#dbeafe",
                  color: "#1e40af",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  border: "1px solid #bfdbfe",
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 3,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LocationOnRounded sx={{ fontSize: 16, color: "#64748b" }} />
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.875rem", color: "#64748b" }}
                >
                  {LoginUserData?.city}, {LoginUserData?.state}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <BusinessRounded sx={{ fontSize: 16, color: "#64748b" }} />
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.875rem", color: "#64748b" }}
                >
                  {LoginUserData?.company_name}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<CreateRounded />}
            onClick={() =>
              showDialog(
                <LawyerProfileForm openLawyerForm={openLawyerForm} />,
                { dialogTitle: "Edit Attorney Profile" }
              )
            }
            sx={{ fontSize: "0.9rem" }}
            size="small"
          >
            Edit Profile
          </Button>
        </Box>

        {/* Stats Section */}
        <Divider sx={{ my: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={6} sm={3}>
            <StatsCard icon={CasesRounded} label="Total Cases" value="162" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard
              icon={TrendingUpRounded}
              label="Success Rate"
              value="94%"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard
              icon={AccountBalanceRounded}
              label="Years Experience"
              value="12"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard icon={StarRounded} label="Rating" value="4.9" />
          </Grid>
        </Grid>
      </Paper>

      {/* Navigation Tabs */}
      <Paper
        elevation={0}
        sx={{ mb: 3, border: "1px solid #e5e7eb", borderRadius: 2 }}
      >
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Overview" />
          <Tab label="Experience" />
          <Tab label="Practice Areas" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{ p: 3, border: "1px solid #e5e7eb", borderRadius: 2 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    color: "#1e293b",
                    mb: 2.5,
                  }}
                >
                  Contact Information
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  <InfoCard
                    icon={LocalPhoneRounded}
                    label="Phone Number"
                    value={LoginUserData?.mobile}
                  />
                  <InfoCard
                    icon={EmailRounded}
                    label="Email Address"
                    value={LoginUserData?.email}
                  />
                  <InfoCard
                    icon={LocationOnRounded}
                    label="Location"
                    value={`${LoginUserData?.city}, ${LoginUserData?.state}`}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Professional Details */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{ p: 3, border: "1px solid #e5e7eb", borderRadius: 2 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    color: "#1e293b",
                    mb: 2.5,
                  }}
                >
                  Professional Details
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  <InfoCard
                    icon={BusinessRounded}
                    label="Law Firm"
                    value={LoginUserData?.company_name}
                  />
                  <InfoCard
                    icon={GavelRounded}
                    label="Attorney ID"
                    value={LoginUserData?.uspto_reg}
                  />
                  <InfoCard
                    icon={DescriptionRounded}
                    label="Tax ID"
                    value={LoginUserData?.tax_id}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Personal Information */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{ p: 3, border: "1px solid #e5e7eb", borderRadius: 2 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    color: "#1e293b",
                    mb: 2.5,
                  }}
                >
                  Personal Information
                </Typography>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoCard
                      icon={PersonRounded}
                      label="Full Name"
                      value={`${LoginUserData?.first_name} ${
                        LoginUserData?.middle_name || ""
                      } ${LoginUserData?.last_name}`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InfoCard
                      icon={CalendarMonthRounded}
                      label="Date of Birth"
                      value={LoginUserData?.birth_date}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InfoCard
                      icon={LocationOnRounded}
                      label="Full Address"
                      value={`${LoginUserData?.address_1}, ${LoginUserData?.city}, ${LoginUserData?.state}, ${LoginUserData?.country} - ${LoginUserData?.pincode}`}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Grid container spacing={3}>
            {/* Work Experience */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{ p: 3, border: "1px solid #e5e7eb", borderRadius: 2 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    color: "#1e293b",
                    mb: 3,
                  }}
                >
                  Work Experience
                </Typography>
                <ExperienceItem
                  title="Senior Partner"
                  company="Smith & Associates"
                  period="2020 - Present"
                  description="Leading a team of 15 attorneys, specializing in corporate law and high-stakes litigation."
                />
                <ExperienceItem
                  title="Associate Attorney"
                  company="Johnson Legal Group"
                  period="2015 - 2020"
                  description="Handled corporate litigation and complex contract negotiations for Fortune 500 companies."
                />
                <ExperienceItem
                  title="Junior Attorney"
                  company="Davis & Partners"
                  period="2012 - 2015"
                  description="Focused on intellectual property law and patent litigation cases."
                />
              </Paper>
            </Grid>

            {/* Education */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{ p: 3, border: "1px solid #e5e7eb", borderRadius: 2 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    color: "#1e293b",
                    mb: 3,
                  }}
                >
                  Education
                </Typography>
                <ExperienceItem
                  title="Harvard Law School"
                  company="Juris Doctor"
                  period="2009 - 2012"
                  description="Graduated Magna Cum Laude, Editor of Harvard Law Review"
                  isEducation={true}
                />
                <ExperienceItem
                  title="Yale University"
                  company="Bachelor of Arts in Political Science"
                  period="2005 - 2009"
                  description="Graduated Summa Cum Laude, Phi Beta Kappa"
                  isEducation={true}
                />
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeTab === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <PracticeAreaCard
                area="Corporate Law"
                cases={45}
                expertise={95}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <PracticeAreaCard
                area="Intellectual Property"
                cases={38}
                expertise={90}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <PracticeAreaCard area="Contract Law" cases={52} expertise={88} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <PracticeAreaCard
                area="Civil Litigation"
                cases={29}
                expertise={85}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <PracticeAreaCard area="Tax Law" cases={18} expertise={75} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <PracticeAreaCard area="Patent Law" cases={22} expertise={80} />
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default ProfilePage;
